import { TZDate } from '@date-fns/tz';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import CurvyOctagon from '../svg/CurvyOctagon.svg';
import {
  placeTicketsIntoWeekMarkers,
  type WeekMarker,
} from './ProgressionBar.utils';
import styles from './ProgressionBar.module.css';
import { BUSINESS_TIME_ZONE, Monthly10k } from '@daymade/common-lib/constants';

interface Props {
  shortMonthName: string;
  weekMarkers: (WeekMarker | undefined)[];
}
const weeksToRender = Array.from({ length: 5 }, (_, i) => i);

const ProgressionBar = (props: Props) => {
  const total = props.weekMarkers.reduce(
    (acc, x) => acc + (x?.earnings || 0),
    0,
  );

  return (
    <div className={styles.progressionBar + ' aboutUsTypographyCaption'}>
      <div className={styles.sideBySide}>
        <p className={styles.introText + ' aboutUsTypographyCaption'}>
          Automatically earn{' '}
          <strong>{Monthly10k.minimumEntriesPerWeek} entries</strong> every week
        </p>
        <ol className={styles.barContainer}>
          {weeksToRender.map((x) => {
            return (
              <li
                className={`${styles.week} ${props.weekMarkers[x]?.earnings ? styles.highlighted : ''} ${props.weekMarkers[x]?.isMidweek ? styles.midWeek : ''}`}
              >
                <div className={styles.weekLabel}>
                  {props.shortMonthName} {props.weekMarkers[x]?.dayNumber}
                </div>
                <div className={`${styles.shapeContainer}`}>
                  <CurvyOctagon className={styles.shape} />
                  {props.weekMarkers[x]?.earnings ? (
                    <span className={styles.pointsEarnt}>
                      +{props.weekMarkers[x].earnings}{' '}
                      <span className="visuallyHidden">points earnt</span>
                    </span>
                  ) : (
                    <span className={styles.pointsEarnt}>
                      +{Monthly10k.minimumEntriesPerWeek}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      <div className={styles.summaryText}>
        Total
        <strong className={styles.summaryTextAlt}>{total}</strong>
        <span className={styles.summaryTextAlt}>entries</span>
      </div>
    </div>
  );
};

export const CurrentUserProgressionBar = observer(() => {
  const userStore = useUserStore();

  const now = new Date();
  const startOfMonth = new TZDate(
    now.getFullYear(),
    now.getMonth(),
    1,
    0,
    0,
    0,
    0,
    BUSINESS_TIME_ZONE,
  );

  const shortMonthName = new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    timeZone: BUSINESS_TIME_ZONE,
  }).format(startOfMonth);

  const days =
    /* Note that February is 1 rather than zero, as months are zero-indexed */
    startOfMonth.getMonth() === 1
      ? Monthly10k.entryAwardDayOfMonthFeb
      : Monthly10k.entryAwardDayOfMonthOther;

  const bigRaffleTickets = userStore.activeTickets.filter(
    (x) => x.drawType.code == Monthly10k.drawTypeCode,
  );
  const weekMarkers: WeekMarker[] = days.map((x) => ({
    dayNumber: x,
    earnings: 0,
    isMidweek: now.getDate() != x,
  }));

  placeTicketsIntoWeekMarkers(startOfMonth, bigRaffleTickets, weekMarkers);

  return (
    <ProgressionBar shortMonthName={shortMonthName} weekMarkers={weekMarkers} />
  );
});

export default ProgressionBar;
